import React, { useEffect, useRef, useState } from 'react'
import {
  CompanyId,
  CreateGoal$Request,
  CustomType,
  GoalId,
  GoalWithProgress,
  ProgressType,
  UpdateGoal$Request,
  UserCompanyDto,
  UserProfileDto,
} from '../../services/api.types'
import { Controller, useForm } from 'react-hook-form'
import { Card } from '../../components/Card/Card'
import YumButton from '../../components/YumButton/YumButton'
import { t } from 'ttag'
import { gray } from '../../styles/material-ui-theme-v2'
import {
  FormGroup,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@material-ui/core'
import { CustomTypeCategory, customTypes, customUnits, getCustomUnitShortTextById } from '../../services/CustomTypes'
import './ChallengeAdmin.scss'
import { convertLocalToUTCDate, convertUTCToLocalDate, getTimePeriodNextMonth } from '../../common/helpers/date'
import Hidden from '@material-ui/core/Hidden'
import ChallengeReview from './components/ChallengeReview/ChallengeReview'
import {
  getAccountEndDate,
  getAccountStartDate,
  getContractPeriod,
  isActivityIndexType,
  isCollectiveType,
  isCustomType,
  isPersistenceType,
  isSimpleDayType,
} from '../../common/helpers/challenges'
import Typography from '@material-ui/core/Typography'

import { isRtl } from '../../i18nInit'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { scrollToBottom } from '../../common/helpers/scroll'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  unitsCountDurationMinuteLong,
  unitsCountEnergyKilocalorieShort,
  unitsDisplayDurationCalorieLong,
} from '../../common/helpers/units'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { endOfDay } from 'date-fns'
import ChallengeStepOrganizer from './components/ChallengeStepOrganizer/ChallengeStepOrganizer'
import ChallengeStepPeriod from './components/ChallengeStepPeriod/ChallengeStepPeriod'
import ChallengeStepDetails from './components/ChallengeStepDetails/ChallengeStepDetails'
import ChallengeStepGoal from './components/ChallengeStepGoal/ChallengeStepGoal'
import ChallengeStepActivityType from './components/ChallengeStepActivityType/ChallengeStepActivityType'
import ChallengeStepConfiguration from './components/ChallengeStepConfiguration/ChallengeStepConfiguration'
import { PointsLogic, PointsLogicTemplate } from './components/ChallengePoints/ChallengePoints'

export interface IChallengeAdminDispatchProps {
  createGoal(payload: CreateGoal$Request): void
  updateGoal(payload: UpdateGoal$Request): void
  getCurrentCompanyGoal(payload: { id?: GoalId | null; silentGoalUpdate?: boolean; token?: string | null }): void
}

export interface IChallengeAdminProps extends IChallengeAdminDispatchProps {
  goalId?: GoalId
  managedCompanies: UserCompanyDto[]
  userCompanies: UserCompanyDto[]
  goals: GoalWithProgress[]
  userDto: UserProfileDto
}

export type GoalType = '' | 'totalAmount' | 'consistentDays' | 'collective' | 'noGoal'

export type FormData = {
  maxFormStep: number

  // Step 0.
  companyId: CompanyId | 'new'
  companyName: string
  newCommunityName: string
  isUserCreatedChallenge: boolean

  // Step 1.
  activityType: ProgressType | CustomTypeCategory | null
  customActivityName?: string

  // Step 2.
  individualLeaderboard: boolean
  groupLeaderboard: boolean
  countryLeaderboard: boolean
  //asCollectiveUnit: boolean
  chatEnabled: boolean
  manualEntryEnabled: boolean
  roundDailyPoints: boolean
  autoJoinEnabled: boolean
  maxManualEntryDays: number

  challengePoints: Array<any>

  // Step 3.
  dateRange: [Date | undefined, Date | undefined]
  days: number

  // Step 4.
  goalType: GoalType
  totalGoal: number | ''
  collectiveGoal: number | ''
  persistenceGoal: number | ''
  dailyGoal: number | ''
  customUnit: string | null
  customType: CustomType | null
  indexTarget: number
  simpleManualEntry: boolean

  // Step 5.
  title: string
  prize: string
  description: string
}

export const activityTypes = [
  {
    value: CustomTypeCategory.STEPS,
    label: t`Steps`,
    usesIntegration: true,
    //activeIcon: StepsActiveIcon,
    //inactiveIcon: StepsInactiveIcon,
  },
  {
    value: CustomTypeCategory.DISTANCE,
    label: t`Distance`,
    usesIntegration: true,
    //activeIcon: DistanceActiveIcon,
    //inactiveIcon: DistanceInactiveIcon,
  },
  {
    label: t`Wellness points`,
    value: CustomTypeCategory.POINTS,
    usesIntegration: true,
    //activeIcon: PointsActiveIcon,
    //inactiveIcon: PointsInactiveIcon,
  },
  {
    label: t`Water drinking`,
    value: CustomTypeCategory.WATER_DRINKING,
    usesIntegration: false,
    //activeIcon: WaterDrinkingActiveIcon,
    //inactiveIcon: WaterDrinkingInactiveIcon,
  },
  {
    value: CustomTypeCategory.CYCLING,
    label: t`Cycling`,
    usesIntegration: true,
    //activeIcon: CyclingActiveIcon,
    //inactiveIcon: CyclingInactiveIcon,
  },
  {
    value: CustomTypeCategory.CALORIES,
    label: unitsDisplayDurationCalorieLong,
    usesIntegration: true,
    //activeIcon: CaloriesActiveIcon,
    //inactiveIcon: CaloriesInactiveIcon,
  },
  {
    value: CustomTypeCategory.SLEEP,
    label: t`Sleep`,
    usesIntegration: false,
    //activeIcon: SleepActiveIcon,
    //inactiveIcon: SleepInactiveIcon,
  },
  {
    value: CustomTypeCategory.OUTDOOR_TIME,
    label: t`Outdoor time`,
    usesIntegration: false,
    //activeIcon: OutdoorActiveIcon,
    //inactiveIcon: OutdoorInactiveIcon,
  },
  {
    value: CustomTypeCategory.MINDFULNESS,
    label: t`Mindfulness`,
    usesIntegration: false,
    //activeIcon: MindfulnessActiveIcon,
    //inactiveIcon: MindfulnessInactiveIcon,
  },
  {
    value: CustomTypeCategory.PLANKING,
    label: t`Planking`,
    usesIntegration: false,
    //activeIcon: PlankingActiveIcon,
    //inactiveIcon: PlankingInactiveIcon,
  },
  {
    value: CustomTypeCategory.RUNNING,
    label: t`Running`,
    usesIntegration: true,
    //activeIcon: RunningActiveIcon,
    //inactiveIcon: RunningInactiveIcon,
  },
  {
    value: CustomTypeCategory.WALKING,
    label: t`Walking`,
    usesIntegration: true,
    //activeIcon: WalkingActiveIcon,
    //inactiveIcon: WalkingInactiveIcon,
  },
  {
    value: CustomTypeCategory.SWIMMING,
    label: t`Swimming`,
    usesIntegration: true,
    //activeIcon: SwimmingActiveIcon,
    //inactiveIcon: SwimmingInactiveIcon,
  },
  {
    value: CustomTypeCategory.YOGA,
    label: t`Yoga`,
    usesIntegration: true,
    //activeIcon: YogaActiveIcon,
    //inactiveIcon: YogaInactiveIcon,
  },
  {
    value: CustomTypeCategory.EXERCISE,
    label: t`Exercise minutes`,
    usesIntegration: true,
    //activeIcon: ExerciseActiveIcon,
    //inactiveIcon: ExerciseInactiveIcon,
  },
  {
    value: CustomTypeCategory.INTENSIVE_MINUTES,
    label: t`Intensive minutes`,
    usesIntegration: true,
    //activeIcon: MinutesActiveIcon,
    //inactiveIcon: MinutesInactiveIcon,
  },
  {
    value: CustomTypeCategory.ALL,
    label: t`Activity index`,
    description: t`YuMuuv formula, requires heart rate data`,
    usesIntegration: true,
    //activeIcon: ActivityIndexActiveIcon,
    //inactiveIcon: ActivityIndexInactiveIcon,
  },
  // New manual entry challenges.
  // Book reading
  {
    value: CustomTypeCategory.BOOK_READING,
    label: t`Book reading`,
    usesIntegration: false,
  },
  // Encouraging someone
  {
    value: CustomTypeCategory.ENCOURAGING,
    label: t`Encouraging someone`,
    usesIntegration: false,
  },
  // Car-free days
  {
    value: CustomTypeCategory.CAR_FREE_DAYS,
    label: t`Car-free days`,
    usesIntegration: false,
  },
  // Alcohol abstinence
  {
    value: CustomTypeCategory.ALCOHOL_ABSTINENCE,
    label: t`Alcohol abstinence`,
    usesIntegration: false,
  },
  // Squatting
  {
    value: CustomTypeCategory.SQUATTING,
    label: t`Squatting`,
    usesIntegration: false,
  },
  // Contrast shower
  {
    value: CustomTypeCategory.CONTRAST_SHOWER,
    label: t`Contrast shower`,
    usesIntegration: false,
  },
  // Finance tracking
  {
    value: CustomTypeCategory.FINANCE_TRACKING,
    label: t`Finance tracking`,
    usesIntegration: false,
  },
  // Healthy cooking
  {
    value: CustomTypeCategory.HEALTHY_COOKING,
    label: t`Healthy cooking`,
    usesIntegration: false,
  },
  // Deep breathing
  {
    value: CustomTypeCategory.DEEP_BREATHING,
    label: t`Deep breathing`,
    usesIntegration: false,
  },

  // Daily Journaling
  {
    value: CustomTypeCategory.DAILY_JOURNALING,
    label: t`Daily journaling`,
    usesIntegration: false,
  },
  // Push-Ups
  {
    value: CustomTypeCategory.PUSH_UPS,
    label: t`Push-ups`,
    usesIntegration: false,
  },
  // Pull-Ups
  {
    value: CustomTypeCategory.PULL_UPS,
    label: t`Pull-ups`,
    usesIntegration: false,
  },
  // Creative Arts
  {
    value: CustomTypeCategory.CREATIVE_ARTS,
    label: t`Creative arts`,
    usesIntegration: false,
  },
  // Socializing with Friends
  {
    value: CustomTypeCategory.SOCIALIZING_WITH_FRIENDS,
    label: t`Socializing with friends`,
    usesIntegration: false,
  },
  // Cold Plunge
  {
    value: CustomTypeCategory.COLD_PLUNGE,
    label: t`Cold plunge`,
    usesIntegration: false,
  },
  // Intermittent Fasting
  {
    value: CustomTypeCategory.INTERMITTENT_FASTING,
    label: t`Intermittent fasting`,
    usesIntegration: false,
  },
  // Gratitude Journaling
  {
    value: CustomTypeCategory.GRATITUDE_JOURNALING,
    label: t`Gratitude journaling`,
    usesIntegration: false,
  },
  // Language learning
  {
    value: CustomTypeCategory.LANGUAGE_LEARNING,
    label: t`Language learning`,
    usesIntegration: false,
  },
  // Hobby Development
  {
    value: CustomTypeCategory.HOBBY_DEVELOPMENT,
    label: t`Hobby development`,
    usesIntegration: false,
  },
  // Natural Light Exposure
  {
    value: CustomTypeCategory.NATURAL_LIGHT_EXPOSURE,
    label: t`Natural light exposure`,
    usesIntegration: false,
  },
  // Smoking Cessation
  {
    value: CustomTypeCategory.SMOKING_CESSATION,
    label: t`Smoking cessation`,
    usesIntegration: false,
  },
  // Phone-Free Time
  {
    value: CustomTypeCategory.PHONE_FREE_TIME,
    label: t`Phone-free time`,
    usesIntegration: false,
  },
  // Caffeine Reduction
  /*{
    value: CustomTypeCategory.CAFFEINE_REDUCTION,
    label: t`Caffeine reduction`,
    usesIntegration: false,
  },*/
  // Screen-Free Meals
  {
    value: CustomTypeCategory.SCREEN_FREE_MEALS,
    label: t`Screen-free meals`,
    usesIntegration: false,
  },
  // Daily Flossing
  {
    value: CustomTypeCategory.DAILY_FLOSSING,
    label: t`Daily flossing`,
    usesIntegration: false,
  },
  // Stress Level Monitoring
  /*{
    value: CustomTypeCategory.STRESS_LEVEL_MONITORING,
    label: t`Stress level monitoring`,
    usesIntegration: false,
  },*/
  // Mood Tracking
  /*{
    value: CustomTypeCategory.MOOD_TRACKING,
    label: t`Mood tracking`,
    usesIntegration: false,
  },*/
  // CUSTOM
  {
    value: CustomTypeCategory.CUSTOM,
    label: t`Custom`,
    usesIntegration: false,
    //activeIcon: CustomActiveIcon,
    //inactiveIcon: CustomInactiveIcon,
  },
]

export const dailyGoalUnit = (activityType: any, customType: string | null, customUnit?: string | null) => {
  return activityType === CustomTypeCategory.CUSTOM
    ? customUnit
    : activityType === CustomTypeCategory.STEPS
    ? t`steps`
    : activityType === CustomTypeCategory.INTENSIVE_MINUTES
    ? unitsCountDurationMinuteLong
    : activityType === CustomTypeCategory.CALORIES
    ? unitsCountEnergyKilocalorieShort
    : activityType === CustomTypeCategory.ALL
    ? '%'
    : customType
    ? getCustomUnitShortTextById(
        customType,
      ) /*+ ' ' + t`of` + ' ' + getCustomTypeTextById(activityType)?.toLowerCase()*/
    : ''
}

export const consistencyUnit = (days: number) => {
  if (!days) return ''
  // translator: [15] out of {30} days
  return t`out of ${days} days`
}

export const calculateDuration = (startDate: Date | undefined, endDate: Date | undefined) => {
  let days = 0
  if (startDate && endDate) {
    // Needs to not work with Daylight Saving Time.
    days = Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24))
  }
  return days
}

const getDefaultValues = (goal?: GoalWithProgress) => {
  if (!goal) return {}
  const {
    voucher,
    progressType,
    endDate: endDateStamp,
    startDate: startDateStamp,
    companyId,
    ownerUserId,
    companyName,
    divisionId,
    dailyStepsGoal,
    activityFormulaTarget,
    collectiveGoal,
    title,
    hidePeopleTab,
    hideGroupsTab,
    hideCountriesTab,
    chatEnabled,
    dailyPersistenceGoal,
    persistenceGoal,
    customType,
    customName,
    customUnit,
    customGoal,
    description,
    manualEntryEnabled,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,
    stepsGoal,
  } = goal
  let activityType: ProgressType | CustomTypeCategory | null = null
  let simpleManualEntry = goal.simpleManualEntry
  if (customType) {
    if (customType === CustomType.CUSTOM) {
      activityType = CustomTypeCategory.CUSTOM
    } else {
      activityType = customUnits.find((cu) => cu.id === customType)?.category || null
    }
  } else {
    switch (progressType) {
      case ProgressType.ALL:
      case ProgressType.FORMULA:
      case ProgressType.PERSISTENCE_ALL:
        activityType = CustomTypeCategory.ALL
        break
      // Deprecated
      case ProgressType.STEPS:
      case ProgressType.PERSISTENCE_STEPS:
      case ProgressType.COLLECTIVE_STEPS:
        activityType = CustomTypeCategory.STEPS
        break
      // Deprecated
      case ProgressType.CALORIES:
      case ProgressType.PERSISTENCE_CALORIES:
      case ProgressType.COLLECTIVE_CALORIES:
        activityType = CustomTypeCategory.CALORIES
        break
      // Deprecated
      case ProgressType.INTENSIVE_MINUTES:
      case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
        activityType = CustomTypeCategory.INTENSIVE_MINUTES
        break
      case ProgressType.CUSTOM:
      case ProgressType.PERSISTENCE_CUSTOM:
      case ProgressType.PERSISTENCE_CUSTOM_BINARY:
        activityType = CustomTypeCategory.CUSTOM
        break
      default:
        alert('Error: unknown progress type')
    }
  }

  let goalType = 'totalAmount'
  if (isPersistenceType(progressType)) goalType = 'consistentDays'

  // Deprecated
  if (progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY) {
    goalType = 'consistentDays'
    simpleManualEntry = true
  }

  if (isCollectiveType(progressType)) goalType = 'collective'

  let totalGoal = null
  if (isCustomType(progressType)) totalGoal = customGoal

  let dailyGoal = dailyPersistenceGoal
  if (progressType === ProgressType.STEPS) dailyGoal = dailyStepsGoal

  const startDate = convertUTCToLocalDate(new Date(startDateStamp))
  const endDate = convertUTCToLocalDate(new Date(endDateStamp))
  const days = calculateDuration(startDate, endDate)

  // Backwards compatibility.
  if (goalType === 'totalAmount' && progressType === ProgressType.STEPS) {
    totalGoal = stepsGoal
  }

  if (goalType === 'totalAmount' && progressType === ProgressType.INTENSIVE_MINUTES) {
    // dailyGoal = Math.floor((customGoal || 0) / days)
    totalGoal = customGoal
  }

  const challengePoints = JSON.parse(goal.pointsLogic) || defaultChallengePoints

  return {
    maxFormStep: 6,

    // Step 0.
    companyId,
    companyName,
    newCommunityName: '',
    isUserCreatedChallenge: !!ownerUserId,

    // Step 1.
    activityType,
    customActivityName: customType === CustomType.CUSTOM ? customName : undefined,

    // Step 2.
    individualLeaderboard: !hidePeopleTab,
    groupLeaderboard: !hideGroupsTab,
    countryLeaderboard: !hideCountriesTab,
    //asCollectiveUnit: isCollectiveType(progressType),
    chatEnabled,
    manualEntryEnabled,
    roundDailyPoints,
    autoJoinEnabled,
    maxManualEntryDays,

    challengePoints,

    // Step 3.
    dateRange: [startDate, endDate],
    days,

    // Step 4.
    goalType,
    totalGoal,
    collectiveGoal,
    persistenceGoal,
    dailyGoal,
    customUnit,
    customType,
    simpleManualEntry,

    indexTarget: isPersistenceType(progressType) ? dailyPersistenceGoal : activityFormulaTarget,

    // Step 5.
    title,
    prize: voucher,
    description,
  }
}

function checkVisible(element: HTMLDivElement | null) {
  if (element === null) return true
  const rect = element.getBoundingClientRect()
  const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight)
  return !(rect.bottom < 0 || rect.top - viewHeight >= 0)
}

function useIsVisible(ref: React.RefObject<HTMLDivElement>, formStep: number) {
  const [isVisible, setIsVisible] = useState(checkVisible(ref.current))

  useEffect(() => {
    function handleResize() {
      setIsVisible(checkVisible(ref.current))
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setTimeout(() => setIsVisible(checkVisible(ref.current)), 0)
  }, [formStep])

  return isVisible
}

let renderCount = 0

const defaultChallengePoints: PointsLogicTemplate[] = [
  {
    category: CustomTypeCategory.STEPS,
    value: 150,
    unit: CustomType.STEPS,
    points: 1,
    customActivityName: '',
    customUnitName: '',
    simpleManualEntry: false,
    dailyGoal: 10000,
    // description: '',
  },
  {
    category: CustomTypeCategory.EXERCISE,
    value: 1,
    unit: CustomType.EXERCISE_MIN,
    points: 1,
    customActivityName: '',
    customUnitName: '',
    simpleManualEntry: false,
    dailyGoal: 30,
    // description: '',
  },
]

export default function ChallengeAdmin(props: IChallengeAdminProps) {
  renderCount++
  /* eslint-disable react/prop-types */
  const { goalId, managedCompanies, userCompanies, goals, getCurrentCompanyGoal /*, hasRoleInCompany*/ } = props

  const formSteps = 6
  const canChangeOrganizer = /*managedCompanies.length && */ !goalId
  const formStartStep = canChangeOrganizer ? 0 : 1
  const [formStep, setFormStep] = useState(formStartStep)
  const [data, setData] = useState({} as FormData)

  const buttonsRef = useRef<HTMLDivElement>(null)
  const areButtonsVisible = useIsVisible(buttonsRef, formStep)

  const orderedCompanies = managedCompanies.sort((a, b) => {
    return !b.ownerCompanyId === !a.ownerCompanyId ? 0 : !b.ownerCompanyId ? 1 : -1
  })

  const { startDate, endDate } = getTimePeriodNextMonth()
  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    ...form
  } = useForm<FormData>({
    shouldUnregister: false,
    defaultValues: /*defaultValues ||*/ {
      maxFormStep: 1,
      companyId: orderedCompanies[0] ? orderedCompanies[0].companyId : 'new',
      newCommunityName: '',
      isUserCreatedChallenge: false,
      activityType: CustomTypeCategory.STEPS,
      individualLeaderboard: true,
      groupLeaderboard: true,
      countryLeaderboard: true,
      roundDailyPoints: true,
      simpleManualEntry: true,
      autoJoinEnabled: true,
      //asCollectiveUnit: false,
      chatEnabled: true,
      manualEntryEnabled: true,
      dateRange: [new Date(startDate), new Date(endDate)],
      days: calculateDuration(new Date(startDate), new Date(endDate)),
      customActivityName: undefined,
      persistenceGoal: '',
      goalType: '', //'totalAmount',
      totalGoal: '',
      collectiveGoal: '',
      dailyGoal: '',
      indexTarget: 100,
      customUnit: '',
      customType: null,
      title: '',
      prize: '',
      description: '',
      challengePoints: defaultChallengePoints,
    },
  })

  const goal: GoalWithProgress | undefined = goals ? goals.find(({ id }) => id === goalId) : undefined

  useEffect(() => {
    register('goalType', { required: formStep === 4 })
  }, [register, formStep])

  if (Capacitor.getPlatform() === 'ios') {
    useEffect(() => {
      const keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', (info) => {
        const buttonsWrapper = document.getElementsByClassName('buttons-wrapper') as HTMLCollectionOf<HTMLElement>
        if (buttonsWrapper[0]) buttonsWrapper[0].style.paddingBottom = info.keyboardHeight - 85 + 'px'
      })
      const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', (info) => {
        const customActivityNameInput = document.getElementById('customActivityName') as HTMLElement
        if (customActivityNameInput === document.activeElement) {
          scrollToBottom()
        }
      })
      const keyboardWillHideListener = Keyboard.addListener('keyboardWillHide', () => {
        const buttonsWrapper = document.getElementsByClassName('buttons-wrapper') as HTMLCollectionOf<HTMLElement>
        if (buttonsWrapper[0]) buttonsWrapper[0].style.paddingBottom = '0px'
      })
      return () => {
        if (keyboardWillShowListener) keyboardWillShowListener.remove()
        if (keyboardDidShowListener) keyboardDidShowListener.remove()
        if (keyboardWillHideListener) keyboardWillHideListener.remove()
      }
    }, [])
  }

  if (goalId) {
    useEffect(() => {
      getCurrentCompanyGoal({ id: goalId })
    }, [])

    const [goalExists, setGoalExists] = useState(false)
    useEffect(() => {
      if (goal) {
        const values = getDefaultValues(goal) as FormData
        reset(values)
        setData(values)
        setGoalExists(true)
      }
    }, [goals])

    if (!goalExists) {
      return <Card>Challenge not found</Card>
    }
  }

  const cancel = () => {
    store.dispatch(push(`/challenges/${goalId}/info`))
  }

  const companyId = watch('companyId')
  const contractStartDate = getAccountStartDate(userCompanies, companyId)
  const contractEndDate = getAccountEndDate(userCompanies, companyId)

  const onSubmit = handleSubmit((newData) => {
    if (formStep === 0) {
      newData.companyName = orderedCompanies.find((uc) => uc.companyId === newData.companyId)?.companyName || ''
      setValue('companyName', newData.companyName)

      const isUserCreatedCommunity = !!managedCompanies.find((uc) => uc.companyId === newData.companyId)?.ownerUserId
      setValue('isUserCreatedChallenge', newData.companyId === 'new' || isUserCreatedCommunity)

      // Logic if we have contract period.
      if (contractStartDate && contractEndDate) {
        const contractDays = calculateDuration(contractStartDate, contractEndDate)
        const contractMoreThanMonth = contractDays > 31
        const contractEnded = contractEndDate < new Date()

        // Set default value as contract period if less than 31 days and has not passed.
        if (!contractMoreThanMonth && !contractEnded) {
          const startDate = contractStartDate
          const endDate = endOfDay(contractEndDate)
          const days = calculateDuration(startDate, endDate)
          setValue('dateRange', [startDate, endDate])
          setValue('days', days)
        }
      }
    }

    if (formStep === 1) {
      if (newData.activityType !== data.activityType && data.maxFormStep > formStep) {
        newData.maxFormStep = formStep
        setValue('maxFormStep', newData.maxFormStep)
      }
      if (newData.isUserCreatedChallenge) {
        setValue('groupLeaderboard', false)
        setValue('countryLeaderboard', false)
      }
    }

    // After date step.
    if (formStep === 3) {
      if (newData.days !== data.days && data.maxFormStep > formStep) {
        newData.maxFormStep = formStep
        setValue('maxFormStep', newData.maxFormStep)
      }

      const defaultPersistenceGoal = Math.ceil(newData.days / 2)
      let newPersistenceGoal = newData.persistenceGoal || defaultPersistenceGoal
      if (newPersistenceGoal > newData.days) newPersistenceGoal = newData.days
      if (newData.persistenceGoal !== newPersistenceGoal) {
        newData.persistenceGoal = newPersistenceGoal
        setValue('persistenceGoal', newData.persistenceGoal)
      }

      if (newData.activityType === CustomTypeCategory.STEPS && !newData.dailyGoal) {
        newData.dailyGoal = 12000
        setValue('dailyGoal', newData.dailyGoal)
      }

      if (newData.activityType === CustomTypeCategory.STEPS && !newData.totalGoal && newData.dailyGoal) {
        newData.totalGoal = Math.ceil(newData.days * newData.dailyGoal)
        setValue('totalGoal', newData.totalGoal)
      }

      if (newData.activityType === CustomTypeCategory.ALL && !newData.indexTarget) {
        newData.indexTarget = 100
        setValue('indexTarget', newData.indexTarget)
      }

      if (
        newData.activityType === CustomTypeCategory.INTENSIVE_MINUTES &&
        (!newData.dailyGoal || newData.dailyGoal > 1440)
      ) {
        newData.dailyGoal = 20
        setValue('dailyGoal', newData.dailyGoal)
      }

      if (isSimpleDayType(newData.activityType as CustomTypeCategory)) {
        newData.dailyGoal = 1
        setValue('dailyGoal', newData.dailyGoal)
      }

      if (!newData.collectiveGoal) {
        newData.collectiveGoal = 1000000
        setValue('collectiveGoal', newData.collectiveGoal)
      }

      const selectedCustomTypeExists = customUnits.some(
        (cu) => cu.category === newData.activityType && cu.id === newData.customType,
      )
      if (!newData.customType || !selectedCustomTypeExists) {
        newData.customType = customUnits.find((cu) => cu.category === newData.activityType)?.id || null
        setValue('customType', newData.customType)
      }
    }

    const next = formStep + 1
    if (formStep < formSteps && newData.maxFormStep < next) {
      newData.maxFormStep = next
      setValue('maxFormStep', newData.maxFormStep)
    }

    setData(newData)

    if (formStep < formSteps) {
      setFormStep(next)
    } else {
      createUpdateChallenge()
    }
  })

  const prevStep = () => {
    const prev = formStep - 1
    if (prev >= formStartStep) {
      setFormStep(prev)
      clearErrors()
    }
  }

  const nextStep = () => {
    onSubmit()
  }

  const getStepTitle = (step: number) => {
    switch (step) {
      case 0:
        return t`Choose organizer`
      case 1:
        return t`Choose activity type`
      case 2:
        return t`Choose configuration`
      case 3:
        return t`Choose period`
      case 4:
        return t`Choose goal`
      case 5:
        return t`Set details`
      case 6:
        return t`Review challenge`
    }
    return ''
  }

  const activityType = watch('activityType')
  const isUserCreatedChallenge = watch('isUserCreatedChallenge')

  const createUpdateChallenge = () => {
    // TODO:
    const id = false
    const {
      // Step 0.
      companyId,
      newCommunityName,

      // Step 1.
      activityType,
      customActivityName,

      // Step 2.
      individualLeaderboard,
      groupLeaderboard,
      countryLeaderboard,
      //asCollectiveUnit,
      chatEnabled,
      manualEntryEnabled,
      autoJoinEnabled,
      maxManualEntryDays,
      roundDailyPoints,

      challengePoints,

      // Step 3.
      dateRange,
      days,

      // Step 4.
      goalType,
      totalGoal,
      //dailyGoal,
      collectiveGoal: collectiveGoalRaw,
      persistenceGoal: persistenceGoalRaw,
      dailyGoal: dailyGoalRaw,
      customUnit: customUnitRaw,
      customType: customTypeRaw,
      indexTarget,

      // Step 5.
      title,
      prize: voucher,
      description,
      simpleManualEntry,
    } = data
    let progressType: any = activityType

    let customType = activityType === CustomTypeCategory.CUSTOM ? CustomType.CUSTOM : null
    const customName = activityType === CustomTypeCategory.CUSTOM ? (customActivityName as string) : null
    let customUnit = customUnitRaw || null

    // Popular custom challenges - all except activity index.
    if (!isActivityIndexType(activityType as ProgressType) && customType !== CustomType.CUSTOM) {
      progressType = CustomTypeCategory.CUSTOM
      customType = customTypeRaw
      customUnit = null

      // Backwards compatibility.
      if (!customType) {
        if (activityType === CustomTypeCategory.STEPS) {
          customType = CustomType.STEPS
        } else if (activityType === CustomTypeCategory.INTENSIVE_MINUTES) {
          customType = CustomType.INTENSIVE_MINUTES
        } else if (activityType === CustomTypeCategory.CALORIES) {
          customType = CustomType.CALORIES
        }
      }
    }

    // Consistency challenges.
    if (goalType === 'consistentDays') {
      if (activityType === CustomTypeCategory.ALL) {
        progressType = ProgressType.PERSISTENCE_ALL
      } else {
        progressType = ProgressType.PERSISTENCE_CUSTOM
      }
    }

    // Collective challenges.
    if (goalType === 'collective') {
      progressType = ProgressType.COLLECTIVE_CUSTOM
    }

    // Needed for backwards compatibility.
    if (activityType === CustomTypeCategory.CUSTOM && simpleManualEntry && goalType === 'consistentDays') {
      progressType = ProgressType.PERSISTENCE_CUSTOM_BINARY
    }

    const startDate = dateRange[0] ? convertLocalToUTCDate(dateRange[0]).getTime() : 0
    const endDate = dateRange[1] ? convertLocalToUTCDate(dateRange[1]).getTime() : 0

    // TODO: In the future, we could support division specific challenges.
    const divisionId = null

    const dailyStepsGoal = progressType === CustomTypeCategory.STEPS ? dailyGoalRaw || null : null
    const activityFormulaTarget = progressType === CustomTypeCategory.ALL ? indexTarget : null
    const collectiveGoal =
      /*asCollectiveUnit*/ goalType === 'collective' && collectiveGoalRaw ? collectiveGoalRaw : null //parseInt(collectiveGoalRaw as string)

    const hidePeopleTab = !individualLeaderboard
    const hideGroupsTab = !groupLeaderboard
    const hideCountriesTab = !countryLeaderboard

    let dailyPersistenceGoal = ['consistentDays'].includes(goalType)
      ? progressType === ProgressType.PERSISTENCE_ALL
        ? indexTarget || null
        : dailyGoalRaw || null
      : null

    // TODO: Remove this after backend bug is fixed.
    if ([ProgressType.PERSISTENCE_CUSTOM_BINARY].includes(progressType)) {
      dailyPersistenceGoal = 0
    }

    const persistenceGoal = isPersistenceType(progressType) ? persistenceGoalRaw || null : 0
    const customGoal =
      (isCustomType(progressType) || progressType === CustomTypeCategory.INTENSIVE_MINUTES) && totalGoal
        ? totalGoal
        : null

    const pointsLogic = activityType === ProgressType.POINTS ? challengePoints : null

    const payload = {
      voucher,
      progressType,
      endDate,
      startDate,
      companyId,
      newCommunityName,
      divisionId,
      dailyStepsGoal,
      activityFormulaTarget,
      collectiveGoal,
      title,
      hidePeopleTab,
      hideGroupsTab,
      hideCountriesTab,
      chatEnabled,
      dailyPersistenceGoal,
      persistenceGoal,
      customType,
      customName,
      customUnit,
      customGoal,
      description,
      manualEntryEnabled,
      pointsLogic,
      roundDailyPoints,
      autoJoinEnabled,
      maxManualEntryDays,
      simpleManualEntry,
    }

    if (!goalId) {
      props.createGoal(payload)
    } else {
      props.updateGoal({
        goalId,
        ...payload,
      })
    }
  }

  const contractPeriod = getContractPeriod(managedCompanies, companyId)

  const creationForm = (
    <form
      onSubmit={onSubmit}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flexGrow: 1 }}>
        <Hidden mdUp>
          <LinearProgress
            variant="determinate"
            value={((formStep + 1 - formStartStep) / (formSteps + 1 - formStartStep)) * 100}
            style={{ marginBottom: 20 }}
          />
        </Hidden>
        <Typography variant="subtitle2" style={{ marginBottom: 18 }}>
          {getStepTitle(formStep)}
        </Typography>

        {formStep === 0 && (
          <ChallengeStepOrganizer
            control={control}
            register={register}
            errors={errors}
            formStep={formStep}
            companyId={companyId}
            userId={props.userDto.userId}
            activityType={activityType as CustomTypeCategory}
            managedCompanies={orderedCompanies}
            userCompanies={userCompanies}
            contractPeriod={contractPeriod}
          />
        )}
        {formStep === 1 && (
          <ChallengeStepActivityType
            setValue={setValue}
            register={register}
            errors={errors}
            formStep={formStep}
            activityType={activityType as CustomTypeCategory}
          />
        )}
        {formStep === 2 && (
          <ChallengeStepConfiguration
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            activityType={activityType as CustomTypeCategory}
            isUserCreatedChallenge={isUserCreatedChallenge}
          />
        )}
        {formStep === 3 && (
          <ChallengeStepPeriod
            control={control}
            errors={errors}
            setValue={setValue}
            companyId={companyId}
            userCompanies={userCompanies}
            contractPeriod={contractPeriod}
            contractStartDate={contractStartDate}
            contractEndDate={contractEndDate}
            isUserCreatedChallenge={isUserCreatedChallenge}
          />
        )}
        {formStep === 4 && (
          <ChallengeStepGoal
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            activityType={activityType as CustomTypeCategory}
            formStep={formStep}
          />
        )}
        {formStep === 5 && <ChallengeStepDetails control={control} errors={errors} formStep={formStep} />}
        {formStep === 6 && (
          <div style={{ marginBottom: 20 }}>
            <ChallengeReview editMode={!!goalId} data={data} formStep={formStep} setFormStep={setFormStep} />
          </div>
        )}
      </div>

      <div style={{ marginTop: '20px', textAlign: 'center', pointerEvents: 'none' }}>
        <div
          style={{
            minHeight: '48px',
          }}
        >
          <div className={!areButtonsVisible ? 'buttons-wrapper buttons-wrapper__sticky' : 'buttons-wrapper'}>
            <div>
              {goal && formStep === 1 ? (
                <YumButton
                  variant="outlined"
                  onClick={cancel}
                  style={{
                    display: 'inline-block',
                    margin: '5px',
                    width: '165px',
                    pointerEvents: 'auto',
                  }}
                >{t`Cancel`}</YumButton>
              ) : (
                ''
              )}
              {formStep > formStartStep && (
                <YumButton
                  variant="outlined"
                  onClick={prevStep}
                  style={{
                    display: 'inline-block',
                    margin: '5px',
                    width: '165px',
                    pointerEvents: 'auto',
                  }}
                >{t`Back`}</YumButton>
              )}
              <YumButton
                onClick={nextStep}
                style={{ display: 'inline-block', margin: '5px', width: '165px', pointerEvents: 'auto' }}
              >
                {formStep === formSteps ? (goalId ? t`Save` : t`Create`) : t`Next`}
              </YumButton>
            </div>
          </div>
        </div>
        <div ref={buttonsRef} style={{ position: 'relative', top: 40 }}></div>
        {/*<br />
        Render count: {renderCount}*/}
      </div>
    </form>
  )

  return (
    <Card>
      {/*<h1 style={{ marginBottom: 40, whiteSpace: 'nowrap' }}>{t`Create challenge`}</h1>*/}
      <Grid container={true} spacing={10}>
        <Grid
          item={true}
          xs={12}
          md={formStep < 6 ? 6 : 12}
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '528px',
            marginLeft: 'auto',
            marginRight: 'auto',
            //height: 'calc(100vh - 80px)',
          }}
        >
          <NotificationsWrapper />
          {creationForm}
        </Grid>
        {formStep < 6 && (
          <Hidden smDown>
            <Grid
              item={true}
              xs={12}
              md={6}
              style={isRtl ? { borderRight: `1px solid ${gray}` } : { borderLeft: `1px solid ${gray}` }}
            >
              <Typography variant="subtitle2" style={{ marginBottom: 18 }}>
                {t`Challenge overview`}
              </Typography>
              <ChallengeReview editMode={!!goalId} data={data} formStep={formStep} setFormStep={setFormStep} />
            </Grid>
          </Hidden>
        )}
      </Grid>
      {/*<pre>{JSON.stringify(data.challengePoints, null, 2)}</pre>*/}
    </Card>
  )
}
